// Operations · Fleet Operations home. HomeHero + 4 KPIs + "what needs attention"
// + version-adoption + vulns-by-severity. All from real endpoints (/fleet,
// /fleet/instances, /operations/jobs, /analytics/validations).
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { HomeHero } from '@/components/shared/HomeHero';
import { Kpi, Sparkline, ProgressBar } from '@/components/shared/Kpi';
import { Card, CardHead, Btn } from '@/components/shared/primitives';
import { Pill } from '@/components/shared/badges';
import { AttnRow } from '@/components/shared/AttnRow';
import { Icon } from '@/components/shared/Icon';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { PERSONA } from '@/lib/persona';
import {
  getFleet, getInstances, getJobs, getValidations, sumVulns,
  type FleetOverview, type FleetInstance, type OpsJob, type ValidationDay,
} from '@/lib/api/ops';

const ACCENT = PERSONA.ops.accent;
const DAY_MS = 86_400_000;

export function OpsHome() {
  const navigate = useNavigate();
  const [fleet, setFleet] = useState<FleetOverview | null>(null);
  const [instances, setInstances] = useState<FleetInstance[]>([]);
  const [jobs, setJobs] = useState<OpsJob[]>([]);
  const [validations, setValidations] = useState<ValidationDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [f, ins, j, v] = await Promise.all([
        getFleet(),
        getInstances().catch(() => []),
        getJobs().catch(() => []),
        getValidations(7).catch(() => []),
      ]);
      setFleet(f);
      setInstances(ins);
      setJobs(j);
      setValidations(v);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load fleet data');
      setFleet(null); setInstances([]); setJobs([]); setValidations([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const vulns = useMemo(() => sumVulns(instances), [instances]);
  const failed24h = useMemo(
    () => jobs.filter((j) => j.status === 'failed' && Date.now() - new Date(j.reported_at || j.enqueued_at).getTime() <= DAY_MS),
    [jobs],
  );
  const staleInstances = useMemo(
    () => instances.filter((i) => {
      const s = (i.health_status || '').toLowerCase();
      return s === 'stale' || s === 'degraded';
    }),
    [instances],
  );
  const trend = useMemo(
    () => [...validations].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(-7).map((d) => d.validation_count),
    [validations],
  );
  const today = trend.length ? trend[trend.length - 1] : 0;

  const installs = fleet?.totals.instances ?? 0;
  const healthy = fleet?.totals.healthy ?? 0;
  const stale = fleet?.totals.stale ?? staleInstances.length;
  const versions = useMemo(
    () => [...(fleet?.version_distribution || [])].sort((a, b) => b.count - a.count),
    [fleet],
  );
  const topVersion = versions[0]?.version;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
        <div className="animate-spin" style={{ width: 32, height: 32, border: '2px solid var(--persona-ops)', borderTopColor: 'transparent', borderRadius: '50%' }} />
      </div>
    );
  }

  const hasAttention = vulns.critical > 0 || failed24h.length > 0 || staleInstances.length > 0;

  return (
    <div>
      <HomeHero
        persona="ops"
        title="Fleet Operations"
        subtitle="Health, security and deployment across every install"
        actions={
          <>
            <Btn icon="refresh-cw" onClick={load}>Refresh</Btn>
            <Btn kind="primary" accent={ACCENT} icon="terminal" onClick={() => navigate('/ops/remote-ops')}>Dispatch Op</Btn>
          </>
        }
      />

      {error && <div style={{ marginBottom: 16 }}><AlertBanner kind="error">Unable to load fleet data: {error}</AlertBanner></div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 14 }}>
        <Kpi label="Installs" value={installs} hint={topVersion ? `top v${topVersion}` : 'no telemetry'} accent={ACCENT} icon="server" onClick={() => navigate('/ops/fleet')} />
        <Kpi label="Healthy" value={`${healthy}/${installs}`} delta={`${stale} stale`} deltaKind={stale > 0 ? 'warn' : 'up'} accent={ACCENT} icon="heart-pulse" />
        <Kpi label="Open Vulnerabilities" value={vulns.critical + vulns.high} hint={`${vulns.critical} critical · ${vulns.high} high`} deltaKind="down" accent={ACCENT} icon="shield-alert" onClick={() => navigate('/ops/vulnerabilities')} />
        <Kpi label="Validations Today" value={today} spark={trend.length >= 2 ? trend : undefined} accent={ACCENT} icon="badge-check" />
      </div>

      {/* What needs attention */}
      <Card style={{ overflow: 'hidden', marginBottom: 16 }}>
        <CardHead title="What needs attention" sub="Operational items requiring action" />
        {!hasAttention ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Nothing needs attention — fleet healthy.</div>
        ) : (
          <div>
            {vulns.critical > 0 && (
              <AttnRow icon="shield-alert" color="#DC2626" action="Triage" onAction={() => navigate('/ops/vulnerabilities')}>
                <strong>{vulns.critical} critical vulnerabilit{vulns.critical !== 1 ? 'ies' : 'y'}</strong> open across the fleet — patch available.
              </AttnRow>
            )}
            {failed24h.length > 0 && (
              <AttnRow icon="x-circle" color="#DC2626" action="Review" onAction={() => navigate('/ops/remote-ops')}>
                <strong>{failed24h.length} failed job{failed24h.length !== 1 ? 's' : ''}</strong> in the last 24h — {Array.from(new Set(failed24h.map((j) => j.op_key))).join(', ')}.
              </AttnRow>
            )}
            {staleInstances.length > 0 && (
              <AttnRow icon="clock" color="#D97706" action="Inspect" onAction={() => navigate('/ops/fleet')}>
                <strong>{staleInstances.length} stale install{staleInstances.length !== 1 ? 's' : ''}</strong> — {staleInstances[0].instance_id} not reporting.
              </AttnRow>
            )}
          </div>
        )}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        {/* Version adoption */}
        <Card>
          <CardHead title="Version adoption" sub={topVersion ? `Most-deployed v${topVersion}` : 'No version telemetry'} right={<a onClick={() => navigate('/ops/updates')} style={linkO}>Updates <Icon name="arrow-right" size={12} /></a>} />
          <div style={{ padding: '16px 20px' }}>
            {versions.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No version data reported.</div>
            ) : (
              versions.map((v) => {
                const pct = installs > 0 ? Math.round((v.count / installs) * 100) : 0;
                const isTop = v.version === topVersion;
                return (
                  <div key={v.version} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, marginBottom: 6 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>v{v.version}</span>
                        {isTop && <Pill bg="#DCFCE7" fg="#15803D" dot>top</Pill>}
                      </span>
                      <span style={{ fontWeight: 600 }}>{v.count} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({pct}%)</span></span>
                    </div>
                    <ProgressBar pct={pct} color={isTop ? '#22C55E' : 'var(--text-muted)'} height={8} />
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* Vulnerabilities by severity */}
        <Card>
          <CardHead title="Vulnerabilities by severity" right={<a onClick={() => navigate('/ops/vulnerabilities')} style={linkO}>All <Icon name="arrow-right" size={12} /></a>} />
          <div style={{ padding: '18px 20px' }}>
            {([['critical', vulns.critical, '#DC2626'], ['high', vulns.high, '#C2410C'], ['medium', vulns.medium, '#A16207'], ['low', vulns.low, '#4338CA']] as const).map(([lvl, n, c]) => {
              const max = Math.max(1, vulns.critical, vulns.high, vulns.medium, vulns.low);
              return (
                <div key={lvl} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <span style={{ width: 70, fontSize: 12.5, textTransform: 'capitalize', color: 'var(--text-secondary)' }}>{lvl}</span>
                  <div style={{ flex: 1 }}><ProgressBar pct={(n / max) * 100} color={c} height={6} /></div>
                  <span style={{ width: 22, textAlign: 'right', fontWeight: 700, fontSize: 13, color: n > 0 ? c : 'var(--text-muted)' }}>{n}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

const linkO: React.CSSProperties = { fontSize: 12.5, color: '#0891B2', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' };

export default OpsHome;
